import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { SelectMultiple, LoadingTip, ConfigProvider } from 'tea-component';
import { getCompaniesCommon } from '@/pages/intelligence/services/filter';
import useDebounce from '@intelligence/components/GenreSelectMiddleware/Supports/_hooks/useDebounce';
import useComputed from './useComputed';
import { getCompanies as getHandledCompanies } from './utils';
import { NewCompanySelectorPropsTypes } from './types';
import { isPlainObject } from 'lodash-es';
import moment from 'moment';
import { useIntl, getLocale } from 'umi';

const NewCompanySelect = (
  props: NewCompanySelectorPropsTypes,
  ref: React.Ref<any>,
): React.ReactElement => {
  const {
    title = 'dbi_company',
    entityType,
    source,
    dataSources,
    metric,
    regionType,
    regions,
    dateType,
    granularity,
    start_date,
    end_date,
    onConfirm,
    onFetchSuccess,
    onFirstFetchSuccess,
    getFetchParams,
    getCompanies,
    customParams = {},
    value,
  } = props;

  const { formatMessage } = useIntl();
  // const { locale } = useIntl();
  const locale = getLocale();
  // 是否首次查询公司列表
  const [isFirstFetch, setIsFirstFetch] = useState(true);
  // 是否加载中
  const [isLoading, setIsLoading] = useState(false);
  // 是否加载中(针对搜索)
  const [isSearching, setIsSearching] = useState(false);
  // 搜索关键字
  const [keywords, setKeywords] = useState('');
  // 增加 lastKeywords 保存 上次搜索时 输入的关键字 屏蔽 翻页后，再搜索时 SelectMultiple组件会 自动触发一次 onScrollBottom 回调 导致首次搜索时，页码不对 接口可能报错 的问题
  const [lastKeywords, setLastKeywords] = useState('');

  // 页码(非搜索模式)
  const [normalPage, setNormalPage] = useState(1);
  // 是否还有更多数据(非搜索模式)
  const [normalHasMore, setNormalHasMore] = useState(false);
  // 公司列表数据(非搜索模式)
  const [normalCompanies, setNormalCompanies] = useState<string[]>([]);

  // 页码(搜索模式)
  const [searchedPage, setSearchedPage] = useState(1);
  // 是否还有更多数据(搜索模式)
  const [searchedHasMore, setSearchedHasMore] = useState(false);
  // 公司列表数据(搜索模式)
  const [searchedCompanies, setSearchedCompanies] = useState<string[]>([]);

  // 已选择公司列表数据(不区分非搜索模式、搜索模式)
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>(value || []);
  const [isAll, setIsAll] = useState(true);
  const [updateValue, setUpdateValue] = useState(false);

  useEffect(() => {
    if (isLoading || !Array.isArray(value)) {
      return;
    }

    if (value.length > 0) {
      setSelectedCompanies(value);
      return;
    }

    if (!keywords && companies.length > 0) {
      setSelectedCompanies(companies);
      return;
    }

    setSelectedCompanies([]);
  }, [value, isLoading]);

  const {
    isClearable,
    companyOptions,
    buttonLabel,

    hasMore,

    page,
    // setPage 为判断区分了 非搜索模式 和 搜索模式，会动态变化为对应的setter(搜索模式、非搜索模式的 page setter不是同一个)，所以在使用了useCallback的方法内，需要将此setter加入该方法的依赖项中
    setPage,

    companies,

    // 根据keywords动态获取 当前状态对应的 has more setter
    getHasMoreSetter,
    // 根据keywords动态获取 当前状态对应的 companies setter
    getCompaniesSetter,
  } = useComputed({
    keywords: lastKeywords,

    normalHasMore,
    setNormalHasMore,
    normalPage,
    setNormalPage,
    normalCompanies,
    setNormalCompanies,

    searchedHasMore,
    setSearchedHasMore,
    searchedPage,
    setSearchedPage,
    searchedCompanies,
    setSearchedCompanies,

    selectedCompanies,
  });

  // 获取公司列表获取接口 入参对象
  const getCompaniesCommonParams = useCallback(
    (params: any) => ({
      entity_type: entityType,
      source,
      metric,
      region_type: regionType,
      regions,
      granularity: granularity || dateType || '',
      start_date: start_date || moment().subtract(30, 'd').format('YYYY-MM-DD'),
      // pageid === 'DAU'
      //   ? moment().subtract(30, 'd').format('YYYY-MM-DD')
      //   : moment().subtract(30, 'd').format('YYYY-MM-DD'),
      end_date: end_date || moment().subtract(1, 'd').format('YYYY-MM-DD'),
      data_sources: dataSources,
      page_size: 500,
      ...params,
    }),
    [
      entityType,
      source,
      dataSources,
      metric,
      regionType,
      regions,
      dateType,
      granularity,
      start_date,
      end_date,
    ],
  );

  // 获取公司列表数据
  const searchCompanies = useCallback(
    async (keywords: string, page: number, isLoadMore?: boolean) => {
      const setHasMore = getHasMoreSetter(keywords);
      const setCompanies = getCompaniesSetter(keywords);
      setIsLoading(true);
      try {
        const fetchParamsGetter =
          getFetchParams instanceof Function ? getFetchParams : getCompaniesCommonParams;
        const fetchMethod = getCompanies instanceof Function ? getCompanies : getCompaniesCommon;
        const otherParams = isPlainObject(customParams) ? customParams : {};
        const newParams = fetchParamsGetter({});
        const params = {
          ...(newParams || {}),
          ...{
            search_name: keywords,
            page,
          },
          source_metric: newParams?.source
            ? {
                [newParams?.source]: newParams?.metric ? [newParams?.metric] : [],
              }
            : {},
          ...otherParams,
        };
        const res = await fetchMethod(params);
        if (
          !res ||
          !res.data ||
          res.data.filter_flag === undefined ||
          res.data.filter_flag === null ||
          !Array.isArray(res.data.company_name)
        )
          return;
        const { filter_flag: companiesHasMore, company_name: companiesData } = res.data;
        let newCompanies: string[] = getHandledCompanies(companiesData);
        if (isLoadMore) {
          newCompanies = [...companies, ...newCompanies];
        }
        newCompanies = [...new Set(newCompanies)];
        setHasMore(companiesHasMore);
        setCompanies(newCompanies);
        if (keywords) {
          // 搜索交互： 搜索为空给人家选
          setSelectedCompanies([]);
        } else if (page === 1 || selectedCompanies?.length === companies?.length) {
          setSelectedCompanies(newCompanies);
        }
        onFetchSuccess instanceof Function && onFetchSuccess(res.data || {});
        if (isFirstFetch) {
          setIsFirstFetch(false);
          onFirstFetchSuccess instanceof Function && onFirstFetchSuccess(res.data || {});
        }
      } catch (e) {
        console.error(e);
        setHasMore(false);
        setCompanies([]);
        setSelectedCompanies([]);
      } finally {
        setIsLoading(false);
      }
    },
    [
      companies,
      selectedCompanies,
      isFirstFetch,
      getFetchParams,
      getCompanies,
      onFetchSuccess,
      onFirstFetchSuccess,
      getHasMoreSetter,
      getCompaniesSetter,
    ],
  );

  const searchDebounce = useDebounce(
    async (...args: [string, number, boolean]) => {
      const keywords = args[0];
      if (!keywords) {
        setLastKeywords('');
        if (selectedCompanies.length === 0) {
          setSelectedCompanies(normalCompanies);
        }
        return;
      }
      setIsSearching(true);
      setSearchedHasMore(false);
      setSearchedPage(1);
      setSearchedCompanies([]);
      try {
        await searchCompanies(...args);
      } catch (e) {
        console.error(e);
      }
      setIsSearching(false);
      setLastKeywords(keywords);
    },
    600,
    [searchCompanies],
  );

  const handleSearch = useCallback(
    (keywords?: string) => {
      const _keywords = keywords || '';
      setKeywords(_keywords);
      searchDebounce(_keywords, 1, false);
    },
    [searchDebounce],
  );

  const handleChange = useCallback(
    (value: string[]) => {
      setUpdateValue(true);
      let payload = value;
      setSelectedCompanies(value);
      if (!keywords) {
        payload = !value?.length || value?.length === companies?.length ? [] : value;
      }
      // onConfirm instanceof Function && onConfirm(payload);
    },
    [keywords, companies, onConfirm],
  );

  const handleClose = useCallback(() => {
    if (!updateValue) {
      return;
    }
    setUpdateValue(false);
    let payload = selectedCompanies;
    if (!selectedCompanies?.length) {
      setSelectedCompanies(companies);
      payload = companies;
    }
    if (!keywords) {
      payload = !payload?.length || payload?.length === companies?.length ? [] : payload;
    }
    onConfirm instanceof Function && onConfirm(payload);
  }, [keywords, companies, selectedCompanies, onConfirm]);

  const handleReset = useCallback(() => {
    setKeywords('');
    setLastKeywords('');
    setNormalHasMore(false);
    setNormalPage(1);
    setSearchedHasMore(false);
    setSearchedPage(1);
    searchCompanies('', 1);

    // onConfirm instanceof Function && onConfirm([]);
  }, [searchCompanies, onConfirm]);

  const handleLoadMore = useCallback(() => {
    if (!hasMore || isLoading) return;
    // 增加 lastKeywords 屏蔽 翻页后，再搜索时 SelectMultiple组件会 自动触发一次 onScrollBottom 回调 导致首次搜索时，页码不对 接口可能报错 的问题
    if (keywords !== lastKeywords) return;
    const newPage = page + 1;
    setPage(newPage);
    searchCompanies(keywords, newPage, true);
  }, [hasMore, isLoading, keywords, lastKeywords, page, setPage, searchCompanies]);

  useImperativeHandle(ref, () => ({
    setSelectedCompanies,
    reset: handleReset,
  }));

  useEffect(() => {
    searchCompanies(keywords, page);
  }, []);
  const onSelectAll = () => {
    // console.log('companyOptions', companyOptions)
    const values = companyOptions?.map((item) => item?.value);
    if (!selectedCompanies?.length) {
      // 全选： 展示清空按钮
      // 点击清除时更新company值（在函数里更新了updateValue）
      handleChange(values);
      // setSelectedCompanies(values);
    } else {
      // 点击清除时更新company值（在函数里更新了updateValue）
      handleChange([]);
      // setSelectedCompanies([])
    }
  };

  // console.log("S", selectedCompanies)

  return (
    <ConfigProvider locale={locale === 'en-US' ? 'en' : 'zh'}>
      <SelectMultiple
        size="m"
        className="select-item"
        appearance="button"
        placeholder={formatMessage({ id: 'dbi_search_any_company' })}
        searchable
        boxSizeSync
        listHeight={254}
        staging={false}
        clearable={false} // isClearable} 统一设计，不展示X按钮（如打开需要解决BUG:点击清除时抛出去的值要更新）
        searchValue={keywords}
        filter={() => !(isLoading && isSearching)}
        value={selectedCompanies || []}
        searchPlaceholder={formatMessage({ id: 'selector_multiple_component_placeholder' })}
        button={
          <div className="dbi-select-button">
            <div className="select-button-text">{formatMessage({ id: title })}</div>
            <div className="select-button-desc">{buttonLabel}</div>
          </div>
        }
        tips={
          <div className="dbi-selects-flex">
            <span className="dbi-selects-flex-lf">{formatMessage({ id: title })}</span>
            <span className="dbi-selects-flex-rt" onClick={onSelectAll}>
              {!selectedCompanies?.length
                ? formatMessage({ id: 'dbi_select_all' })
                : formatMessage({ id: 'dbi_clear_all' })}
            </span>
          </div>
        }
        options={companyOptions}
        virtual
        // allOption={{ value: 'All', text: 'All' }}
        onSearch={handleSearch}
        onClose={handleClose}
        onChange={handleChange}
        onScrollBottom={handleLoadMore}
        // tips=""
        footer={isLoading && <LoadingTip className="smallLoading" />}
      />
    </ConfigProvider>
  );
};

export default forwardRef(NewCompanySelect);
